import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import { Zap, Plus, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const EVENT_TYPES = [
  { key: "critical_alert", label: "Critical Alerts" },
  { key: "overdue_maintenance", label: "Overdue Maintenance" },
  { key: "work_order_created", label: "Work Order Created" },
  { key: "work_order_completed", label: "Work Order Completed" },
  { key: "blueprint_uploaded", label: "Blueprint Uploaded" },
  { key: "scan_completed", label: "Scan Completed" },
  { key: "quality_event", label: "Quality Event" },
];

export default function PowerAutomatePage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [cfgR, histR] = await Promise.all([
        axios.get(`${API}/admin/power-automate-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/power-automate/history`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgR.data);
      setHistory(histR.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/power-automate-config`, config, { headers: getAuthHeaders() });
      toast.success("Power Automate configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const testFlow = async (url) => {
    try {
      const r = await axios.post(`${API}/admin/power-automate-test`, { trigger_url: url }, { headers: getAuthHeaders() });
      toast.success(r.data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Test failed"); }
  };

  const addFlow = () => {
    const flows = config.flows || [];
    setConfig({ ...config, flows: [...flows, { id: Date.now().toString(), name: "", trigger_url: "", event_types: [] }] });
  };

  const updateFlow = (idx, field, value) => {
    const flows = [...(config.flows || [])];
    flows[idx] = { ...flows[idx], [field]: value };
    setConfig({ ...config, flows });
  };

  const removeFlow = (idx) => {
    const flows = [...(config.flows || [])];
    flows.splice(idx, 1);
    setConfig({ ...config, flows });
  };

  const toggleFlowEvent = (idx, eventKey) => {
    const flows = [...(config.flows || [])];
    const events = flows[idx].event_types || [];
    flows[idx].event_types = events.includes(eventKey) ? events.filter(e => e !== eventKey) : [...events, eventKey];
    setConfig({ ...config, flows });
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64 text-[#64748B] font-mono">Loading...</div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="power-automate-page">
        <PageHeader title="Microsoft Power Automate" subtitle="Trigger flows on Vault events for automated workflows" testId="pa-title">
          <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="pa-save-btn">{saving ? "Saving..." : "Save Configuration"}</Button>
        </PageHeader>

        <IntegrationHealthBadge integration="power_automate" onTestConnection={async () => {
          if (!config.flows?.length) return { status: "not_configured", message: "No flows configured" };
          return await testFlow(config.flows[0].trigger_url).then(() => ({ status: "success", message: "Flow triggered" })).catch(() => ({ status: "error", message: "Test failed" }));
        }} />

        <div className="bg-white border border-[#E2E8F0] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm text-[#0F172A] flex items-center gap-2"><Zap size={16} /> Flows</h3>
            <div className="flex items-center gap-3">
              <Label className="text-sm">Enabled</Label>
              <button onClick={() => update("enabled", !config.enabled)} className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid="pa-enabled-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          {(config.flows || []).map((flow, idx) => (
            <div key={flow.id} className="border border-[#E2E8F0] p-4 mb-3" data-testid={`flow-${idx}`}>
              <div className="flex gap-3 mb-3">
                <div className="flex-1"><Label className="text-xs">Flow Name</Label><Input value={flow.name} onChange={e => updateFlow(idx, "name", e.target.value)} className="mt-1 rounded-none text-sm" placeholder="e.g., Alert Channel" /></div>
                <div className="flex-[2]"><Label className="text-xs">Trigger URL (HTTP POST)</Label><Input value={flow.trigger_url} onChange={e => updateFlow(idx, "trigger_url", e.target.value)} className="mt-1 rounded-none font-mono text-sm" placeholder="https://prod-xx.westus.logic.azure.com/..." /></div>
                <div className="flex items-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => testFlow(flow.trigger_url)} className="rounded-none text-xs" data-testid={`test-flow-${idx}`}>Test</Button>
                  <Button size="sm" variant="ghost" onClick={() => removeFlow(idx)} className="text-[#DC2626]"><Trash2 size={14} /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {EVENT_TYPES.map(et => (
                  <button key={et.key} onClick={() => toggleFlowEvent(idx, et.key)} className={`px-2 py-1 text-xs font-bold border transition-colors ${(flow.event_types || []).includes(et.key) ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#2563EB]"}`}>
                    {et.label}
                  </button>
                ))}
                <span className="text-[10px] text-[#94A3B8] self-center">{!(flow.event_types || []).length && "(triggers on ALL events)"}</span>
              </div>
            </div>
          ))}
          <Button onClick={addFlow} variant="outline" className="rounded-none border-dashed border-[#94A3B8] text-[#64748B] w-full" data-testid="add-flow-btn"><Plus size={14} className="mr-1" />Add Flow</Button>
        </div>

        {history.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-3 flex items-center gap-2"><Clock size={16} /> Trigger History</h3>
            <div className="space-y-1">
              {history.slice(0, 20).map(h => (
                <div key={h.id} className="flex items-center justify-between p-2 bg-[#FAFAFA] border border-[#E2E8F0] text-sm">
                  <span className="font-mono text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString()}</span>
                  <span className="font-bold text-[#2563EB] text-xs uppercase">{h.event_type}</span>
                  <span className="text-xs text-[#64748B]">{h.flows_triggered} flow(s)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
